/**
 * RichTextEditor — TipTap-based WYSIWYG editor used by the booking-form
 * builder for `rich_text` display fields. Returns HTML string content via
 * `onChange`. Read-only preview is available via the `<RichTextView />`
 * helper exported alongside.
 *
 * Design philosophy:
 *   - All admin-authored content; render with dangerouslySetInnerHTML on
 *     the public side. No XSS risk from invitee input.
 *   - Toolbar exposes: bold / italic / underline / strike / H1-H3 /
 *     bullet+ordered lists / link / image / blockquote / code / clear.
 *   - Inline link prompts via window.prompt to keep the dependency footprint
 *     light (no shadcn Dialog wiring needed).
 */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Quote,
  Code,
  Eraser,
} from "lucide-react";
import { useEffect } from "react";

const TOOLBAR_BTN = "p-1.5 rounded hover:bg-[#005e7a]/10 text-[#4a4a52] transition-colors disabled:opacity-30";
const TOOLBAR_BTN_ACTIVE = "p-1.5 rounded bg-[#005e7a] text-white";

export function RichTextEditor({ value, onChange, placeholder = "Write something…", testid }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({ inline: false, HTMLAttributes: { class: "rounded-lg max-w-full h-auto my-3" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap emits "<p></p>" for empty — normalize to "" so consumers can
      // detect "no content" with a simple falsy check.
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Keep editor in sync when parent swaps the value (e.g. switching fields).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (incoming !== current && incoming !== (current === "<p></p>" ? "" : current)) {
      editor.commands.setContent(incoming, false);
    }
    // eslint-disable-next-line
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt("Image URL", "https://");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="border border-[#1a1a1f]/15 rounded-md bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-[#1a1a1f]/10 bg-[#f5efe6]/40">
        <ToolBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold" testid="rte-bold"><Bold className="w-4 h-4" /></ToolBtn>
        <ToolBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic" testid="rte-italic"><Italic className="w-4 h-4" /></ToolBtn>
        <ToolBtn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline" testid="rte-underline"><UnderlineIcon className="w-4 h-4" /></ToolBtn>
        <ToolBtn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough" testid="rte-strike"><Strikethrough className="w-4 h-4" /></ToolBtn>
        <Sep />
        <ToolBtn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1" testid="rte-h1"><Heading1 className="w-4 h-4" /></ToolBtn>
        <ToolBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2" testid="rte-h2"><Heading2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3" testid="rte-h3"><Heading3 className="w-4 h-4" /></ToolBtn>
        <Sep />
        <ToolBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list" testid="rte-ul"><List className="w-4 h-4" /></ToolBtn>
        <ToolBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list" testid="rte-ol"><ListOrdered className="w-4 h-4" /></ToolBtn>
        <ToolBtn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote" testid="rte-quote"><Quote className="w-4 h-4" /></ToolBtn>
        <ToolBtn active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code block" testid="rte-code"><Code className="w-4 h-4" /></ToolBtn>
        <Sep />
        <ToolBtn active={editor.isActive("link")} onClick={setLink} title="Link" testid="rte-link"><LinkIcon className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={addImage} title="Image" testid="rte-image"><ImageIcon className="w-4 h-4" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting" testid="rte-clear"><Eraser className="w-4 h-4" /></ToolBtn>
      </div>
      <EditorContent
        editor={editor}
        className="bloq-rte-content prose prose-sm max-w-none px-3 py-2 min-h-[120px] focus:outline-none"
      />
    </div>
  );
}

function ToolBtn({ active, onClick, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={active ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span className="w-px h-5 bg-[#1a1a1f]/10 mx-1 self-stretch" aria-hidden="true" />;
}

/**
 * Read-only render. Admin-authored content only — safe to inject as HTML.
 */
export function RichTextView({ html, className = "" }) {
  return (
    <div
      className={`bloq-rte-content prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: html || "" }}
    />
  );
}
